import React from 'react';
import { Button } from '@material-ui/core';
import PropTypes from 'prop-types';

import Modal from './Modal';

// Constants
import { AlertTypes } from '../constants';
import { T, SRP_ALERTS, DRILLS, drillName, onColor } from '../constants/tokens';

/**
 * Confirms setting, or clearing, the school-wide status, or starting a drill.
 *
 * The confirm button takes the status colour, so the thing you are about to
 * do is the loudest element in the dialog.
 *
 * A drill is chosen in the drill picker, never here: it used to be a
 * checkbox on this dialog, one click away from the real alert.
 */
export default function ConfirmStatusModal({ closeModal, showModal, setStatus, desiredStatus, clearStatus, isDrill = false }) {
  const setting = desiredStatus != null;
  const color = setting ? desiredStatus.getColor() : null;

  // AlertType has no `name`; this read undefined and rendered
  // "Confirm undefined Alert to entire school?".
  const statusName = setting ? desiredStatus.getDashboardStatus() : null;
  const drill = setting && isDrill;
  const dispatches = setting && statusName === 'Emergency';

  // By name: a rehydrated status is a fresh AlertType, not the constant.
  const details = drill ? DRILLS : SRP_ALERTS;
  const key = setting
    ? Object.keys(details).find((k) => AlertTypes[k]?.getDashboardStatus() === statusName)
    : null;
  const action = key ? details[key].action : null;
  const name = drill ? drillName(statusName) : statusName;

  const body = !setting
    ? 'Every room returns to Normal and badges stop showing an alert.'
    : [
      'Every badge in the school will show this alert.',
      action ? action + '.' : null,
      drill
        ? 'As a drill, no red alert texts are sent and emergency services are not called. A real alert raised during the drill ends it.'
        // A school can switch the automatic call off, so the call is not promised.
        : (dispatches ? 'Red alert contacts are texted, and emergency services are called unless automatic calls are switched off for this school.' : null)
    ].filter(Boolean).join(' ');

  return (
    <Modal
      open={showModal}
      onClose={closeModal}
      eyebrow={setting ? (drill ? 'Confirm drill' : 'Confirm alert') : 'Confirm'}
      dotColor={setting ? color.main : T.mutedSoft}
      title={setting
        ? (drill ? 'Start ' + (/^[aeiou]/i.test(name) ? 'an ' : 'a ') + name.toLowerCase() + ' for the whole school?' : 'Set ' + statusName + ' for the whole school?')
        : 'Clear every room status?'}
      body={body}
      actions={
        <>
          <Button variant='outlined' onClick={closeModal}>Cancel</Button>
          {setting ?
            <Button
              variant='contained'
              onClick={() => setStatus(desiredStatus, drill)}
              style={{ background: color.main, color: onColor(color.main) }}
            >
              {drill ? 'Start ' + name.toLowerCase() : 'Set ' + statusName}
            </Button>
            :
            <Button variant='contained' color='primary' onClick={() => clearStatus()}>
              Clear all
            </Button>}
        </>
      }
    />
  );
}

ConfirmStatusModal.propTypes = {
  showModal: PropTypes.bool,
  closeModal: PropTypes.func,
  setStatus: PropTypes.func,
  desiredStatus: PropTypes.object,
  clearStatus: PropTypes.func,
  /** Start the status as a drill, chosen in the drill picker. */
  isDrill: PropTypes.bool
};
